import CryptoJS from "crypto-js";
import UploadChunkStatus from "../model/enums/UploadChunkStatus";
import UploadFileType from "../model/enums/UploadFileType";
import UploadRequest from "../model/UploadRequest";
import UploadResponse from "../model/UploadResponse";
import APIEndPoints from "../utils/APIEndPoints";
import { IApiClient } from "./ApiClient";
import UploadFileInformation from "../model/UploadFileInformation";
import UploadFileStatus from "../model/enums/UploadFileStatus";
import UploadManagerStatus from "../model/enums/UploadManagerStatus";
import UploadChunkInformation from "../model/UploadChunkInformation";
import UploadChunkResponse from "../model/UploadChunkResponse";
import UploadManagerProgressReport from "../model/UploadManagerProgressReport";
import UploadManagerProgressReportItem from "../model/UploadManagerProgressReportItem";
import IIndexedObject from "../utils/IIndexedObject";
import UploadFileResult from "../model/UploadFileResult";

interface IUploadManagerProps {
  apiClient: IApiClient;
  progressCallback?: (info: UploadManagerProgressReport) => void;
  chunkSize?: number;
  maxConnections?: number;
  maxUploadRetries?: number;
}

class UploadManager {
  private apiClient: IApiClient;
  private progressCallback: (info: UploadManagerProgressReport) => void;
  private chunkSize: number;
  private maxConnections: number;
  private maxUploadRetries: number;
  private filesPending: UploadFileInformation[] = [];
  private fileUniqueId: number = 1;
  private chunksPending: UploadChunkInformation[] = [];
  private chunksProcessed: number = 0;
  private openConnections: Set<Promise<any>> = new Set();
  private uploadingInProgress: boolean = false;
  private DEBUG: boolean = true;

  constructor({
    apiClient,
    progressCallback = () => {},
    chunkSize = 1024 * 1024 * 10,
    maxConnections = 1,
    maxUploadRetries = 4,
  }: IUploadManagerProps) {
    this.apiClient = apiClient;
    this.progressCallback = progressCallback;
    this.chunkSize = chunkSize;
    this.maxConnections = maxConnections;
    this.maxUploadRetries = maxUploadRetries;
  }

  public addFiles(
    identifier: string,
    uploadFileType: UploadFileType,
    files: FileList | File[]
  ): boolean {
    if (this.uploadingInProgress) {
      return false;
    }
    for (let i = 0; i < files.length; i++) {
      this.filesPending.push(
        new UploadFileInformation(
          this.fileUniqueId++,
          identifier,
          uploadFileType,
          files[i],
          this.chunkSize
        )
      );
    }
    return true;
  }

  public addFile(
    identifier: string,
    uploadFileType: UploadFileType,
    file: File
  ): boolean {
    if (this.uploadingInProgress) {
      return false;
    }
    this.filesPending.push(
      new UploadFileInformation(
        this.fileUniqueId++,
        identifier,
        uploadFileType,
        file,
        this.chunkSize
      )
    );
    return true;
  }

  public getFiles(identifier: string): UploadFileInformation[] {
    const response: UploadFileInformation[] = [];
    for (let i = 0; i < this.filesPending.length; i++) {
      const fileInfo = this.filesPending[i];
      if (fileInfo.identifier === identifier) {
        response.push(this.filesPending[i]);
      }
    }
    return response;
  }

  public removeFiles(identifier: string): boolean {
    if (this.uploadingInProgress) {
      return false;
    }
    for (let i = 0; i < this.filesPending.length; i++) {
      const fileInfo = this.filesPending[i];
      if (fileInfo.identifier === identifier) {
        this.filesPending.splice(i, 1);
        i--;
      }
    }
    return true;
  }

  public replaceFiles(
    identifier: string,
    uploadFileType: UploadFileType,
    files: FileList | File[] | null
  ): boolean {
    if (this.uploadingInProgress) {
      return false;
    }

    this.removeFiles(identifier);

    if (files != null) {
      this.addFiles(identifier, uploadFileType, files);
    }

    return true;
  }

  public resetUploads() {
    // make sure the list of pending chunks is empty
    this.chunksPending = [];
    // reset the status for all files that have upload errors
    for (let f = 0; f < this.filesPending.length; f++) {
      const fileInfo = this.filesPending[f];
      // we reset only the files that failed to upload
      if (fileInfo.uploadStatus === UploadFileStatus.FAILED) {
        fileInfo.uploadStatus = UploadFileStatus.PENDING;
      }
    }
  }

  public hasAnyFilesPending() {
    return this.filesPending.length > 0;
  }

  public async uploadFiles(): Promise<
    UploadManagerStatus | UploadFileResult[]
  > {
    return new Promise<UploadManagerStatus | UploadFileResult[]>(
      async (resolve, reject) => {
        // it is not allowed to call uploadFiles multiple times, especially in parallel
        const startUploadResult = this.canStartUpload();
        if (
          startUploadResult !== UploadManagerStatus.SUCCESS_UPLOAD_CAN_START &&
          startUploadResult !== UploadManagerStatus.ERROR_ALL_FILES_PROCESSED
        ) {
          reject(startUploadResult);
        }

        // if all the files have been uploaded, skip the process and return result
        if (
          startUploadResult === UploadManagerStatus.SUCCESS_UPLOAD_CAN_START
        ) {
          // create the list of chunks that will be uploaded, from all the files
          for (let f = 0; f < this.filesPending.length; f++) {
            const fileInfo = this.filesPending[f];
            // skip already uploaded files (this is for resume)
            if (fileInfo.uploadStatus === UploadFileStatus.COMPLETE) {
              continue;
            }
            // add the chunks of the file to the upload list
            for (let c = 0; c < fileInfo.totalChunks; c++) {
              this.chunksPending.push(
                new UploadChunkInformation(
                  fileInfo,
                  c,
                  UploadChunkStatus.PENDING
                )
              );
            }
          }

          this.d("---> BEGIN upload implementation");
          // we can start the upload => mark status apropriately
          this.uploadingInProgress = true;
          this.chunksProcessed = 0;

          this.reportProgress();
          await this.implementUploads();

          this.uploadingInProgress = false;
          this.d("---> END upload implementation");
        }

        // create the response
        const response: UploadFileResult[] = [];
        for (let f = 0; f < this.filesPending.length; f++) {
          const fileInfo = this.filesPending[f];
          // if there is an upload error, the server ID should be discarded
          const serverId =
            fileInfo.uploadStatus === UploadFileStatus.COMPLETE
              ? fileInfo.uploadID
              : undefined;

          response.push(
            new UploadFileResult(
              fileInfo.identifier,
              fileInfo.file.name,
              serverId,
              fileInfo.uploadStatus
            )
          );
        }

        resolve(response);
      }
    );
  }

  // function that checks what is the next chunk to upload
  private async implementUploads() {
    while (this.chunksProcessed < this.chunksPending.length) {
      this.d("------ WHILE -------> ");

      // identify the chunk to be processed
      for (
        let c = 0;
        c < this.chunksPending.length &&
        this.openConnections.size < this.maxConnections;
        c++
      ) {
        const chunkInfo = this.chunksPending[c];

        // check if the chunk needs to be processed
        if (
          chunkInfo.uploadStatus === UploadChunkStatus.UPLOADING ||
          chunkInfo.uploadStatus === UploadChunkStatus.UPLOADED ||
          chunkInfo.uploadStatus === UploadChunkStatus.FAILED
        ) {
          // we are not processing this chunk
          continue;
        }

        // check if the file status allows us to process the chunks
        if (
          chunkInfo.fileInfo.uploadStatus === UploadFileStatus.WAITING ||
          chunkInfo.fileInfo.uploadStatus === UploadFileStatus.COMPLETE ||
          chunkInfo.fileInfo.uploadStatus === UploadFileStatus.FAILED
        ) {
          // we are not processing the chunks from this file
          continue;
        }

        if (chunkInfo.uploadStatus === UploadChunkStatus.PENDING) {
          // check if the upload of the file that owns this chunk has been initiated
          if (chunkInfo.fileInfo.uploadID < 0) {
            this.d(
              "Initiate upload: #" + chunkInfo.fileInfo.startOrCompleteRetries,
              chunkInfo.fileInfo
            );
            // initiate the upload of this file
            chunkInfo.fileInfo.uploadStatus = UploadFileStatus.WAITING;

            const uploadPromise = this.initiateUpload(
              chunkInfo.fileInfo
            ).finally(() => {
              this.openConnections.delete(uploadPromise);
              this.d(
                "Removed promise for initiate upload. Live connections: ",
                this.openConnections.size
              );
            });
            this.openConnections.add(uploadPromise);
          } else {
            // the upload for the file has been initiated => upload chunks
            this.d("Upload chunk: ", chunkInfo);

            chunkInfo.uploadStatus = UploadChunkStatus.UPLOADING;
            chunkInfo.fileInfo.uploadingChunks++;

            const uploadPromise = this.uploadChunk(chunkInfo).finally(() => {
              this.openConnections.delete(uploadPromise);
              this.d(
                "Removed promise for chunk. Live connections: ",
                this.openConnections.size
              );
            });
            this.openConnections.add(uploadPromise);
          }
        } else if (chunkInfo.uploadStatus === UploadChunkStatus.RETRY) {
          this.d(
            "RE-upload chunk, retry number: " + chunkInfo.uploadRetries,
            chunkInfo
          );

          if (chunkInfo.uploadRetries < this.maxUploadRetries) {
            chunkInfo.uploadRetries++;
            chunkInfo.uploadStatus = UploadChunkStatus.UPLOADING;
            chunkInfo.fileInfo.uploadingChunks++;

            const uploadPromise = this.uploadChunk(chunkInfo).finally(() => {
              this.openConnections.delete(uploadPromise);
              this.d(
                "Removed promise for chunk RE-upload. Live connections: ",
                this.openConnections.size
              );
            });
            this.openConnections.add(uploadPromise);
          } else {
            this.d("File upload ABORTED --> ", chunkInfo.fileInfo.file.name);
            // we have retried the maximum allowed ammount => abort the upload for this file
            chunkInfo.uploadStatus = UploadChunkStatus.FAILED;
            chunkInfo.fileInfo.uploadStatus = UploadFileStatus.FAILED;
            this.chunksProcessed +=
              chunkInfo.fileInfo.totalChunks -
              chunkInfo.fileInfo.uploadedChunks;
          }
        } else if (
          chunkInfo.uploadStatus === UploadChunkStatus.SIGNAL_COMPLETE_FILE
        ) {
          this.d(
            "Complete the upload for file: " +
              chunkInfo.fileInfo.startOrCompleteRetries,
            chunkInfo
          );
          // this is the last chunk from the file, we need to complete the upload
          chunkInfo.fileInfo.uploadStatus = UploadFileStatus.WAITING;
          // mark a new upload completion attempt
          chunkInfo.fileInfo.startOrCompleteRetries++;

          const uploadPromise = this.completeUpload(chunkInfo).finally(() => {
            this.openConnections.delete(uploadPromise);
            this.d(
              "Removed promise for complete upload. Live connections: ",
              this.openConnections.size
            );
          });
          this.openConnections.add(uploadPromise);
        }
      }
      // wait for at least one connection to complete before continuing
      if (this.openConnections.size > 0) {
        this.d(
          "Should wait for some promises to complete. Live connections: " +
            this.openConnections.size
        );
        await Promise.race(this.openConnections);
        this.d("At least one promise completed!");
      }
    }
    this.d(
      "---- WHILE ENDED -----> live connections: ",
      this.openConnections.size
    );
    // Ensure all remaining connections finish
    if (this.openConnections.size > 0) {
      await Promise.all(this.openConnections);
    }
  }

  private async initiateUpload(fileInfo: UploadFileInformation) {
    // check how many times we have tried to initiate the upload
    if (fileInfo.uploadStatus === UploadFileStatus.RETRY) {
      if (fileInfo.startOrCompleteRetries < this.maxUploadRetries) {
        fileInfo.startOrCompleteRetries++;
      } else {
        this.d("Upload FAILED, ", fileInfo);
        fileInfo.uploadStatus = UploadFileStatus.FAILED;
        return;
      }
    }

    const arrayBuffer = await new Response(fileInfo.file).arrayBuffer();
    const fileChecksum = CryptoJS.MD5(
      CryptoJS.lib.WordArray.create(arrayBuffer)
    ).toString();

    // launch the request
    try {
      const response = await this.apiClient.post<UploadResponse>({
        url: APIEndPoints.UPLOADS.START,
        body: new UploadRequest(
          fileInfo.file.name,
          fileChecksum,
          fileInfo.uploadFileType,
          fileInfo.totalChunks
        ),
      });

      fileInfo.uploadID = response.id;
      fileInfo.uploadStatus = UploadFileStatus.IN_PROGRESS;
    } catch (error) {
      fileInfo.uploadStatus = UploadFileStatus.RETRY;
    }
  }

  private async uploadChunk(chunkInfo: UploadChunkInformation) {
    const start = chunkInfo.chunkIndexInFile * this.chunkSize;
    const end = (chunkInfo.chunkIndexInFile + 1) * this.chunkSize;
    const formData = new FormData();
    const blobSlice = chunkInfo.fileInfo.file.slice(start, end);
    const arrayBuffer = await new Response(blobSlice).arrayBuffer();
    const blobChecksum = CryptoJS.MD5(
      CryptoJS.lib.WordArray.create(arrayBuffer)
    ).toString();

    formData.append(
      "metadata",
      JSON.stringify({
        chunk_number: chunkInfo.chunkIndexInFile,
        md5_checksum: blobChecksum,
      })
    );

    formData.append("chunk", blobSlice);

    try {
      const response = await this.apiClient.post<UploadChunkResponse>({
        url: APIEndPoints.UPLOADS.CHUNK(chunkInfo.fileInfo.uploadID),
        body: formData,
        isUpload: true,
      });

      if (response.status === UploadChunkStatus.UPLOADED) {
        chunkInfo.uploadStatus = UploadChunkStatus.UPLOADED;
        chunkInfo.fileInfo.uploadedChunks++;
        // check if this file has all chunks uploaded
        if (
          chunkInfo.fileInfo.uploadedChunks >= chunkInfo.fileInfo.totalChunks
        ) {
          chunkInfo.fileInfo.startOrCompleteRetries = 0;
          chunkInfo.uploadStatus = UploadChunkStatus.SIGNAL_COMPLETE_FILE;
        } else {
          // the processing of this chunk completes when the upload is complete
          this.chunksProcessed++;
        }
      } else {
        chunkInfo.uploadStatus = UploadChunkStatus.RETRY;
      }
    } catch (error) {
      this.d("uploadChunk ERROR: ", error);
      chunkInfo.uploadStatus = UploadChunkStatus.RETRY;
    }

    chunkInfo.fileInfo.uploadingChunks--;
  }

  private async completeUpload(chunkInfo: UploadChunkInformation) {
    const fileInfo = chunkInfo.fileInfo;
    // check the number of chunks uploaded successfully
    if (fileInfo.uploadedChunks < fileInfo.totalChunks) {
      return;
    }
    // check how many complete calls have been made
    if (fileInfo.startOrCompleteRetries >= this.maxUploadRetries) {
      fileInfo.uploadStatus = UploadFileStatus.FAILED;
    }

    try {
      const response = await this.apiClient.post<UploadResponse>({
        url: APIEndPoints.UPLOADS.COMPLETE(fileInfo.uploadID),
        body: {},
      });
      fileInfo.uploadStatus = UploadFileStatus.COMPLETE;
      chunkInfo.uploadStatus = UploadChunkStatus.UPLOADED;
      this.chunksProcessed++;
      this.d("Upload complete for file: " + fileInfo.file.name, response);
    } catch (error) {
      fileInfo.startOrCompleteRetries++;
    }
  }

  private canStartUpload(): UploadManagerStatus {
    // check if there are any pending files
    if (this.filesPending.length === 0) {
      return UploadManagerStatus.ERROR_NO_FILES_TO_UPLOAD;
    }
    // check if we have an upload in progress
    if (this.uploadingInProgress) {
      return UploadManagerStatus.ERROR_UPLOAD_IN_PROGRESS;
    }
    // check if all files have been already uploaded
    var filesToUpload = 0;
    for (let i = 0; i < this.filesPending.length; i++) {
      const fileInfo = this.filesPending[i];
      if (
        fileInfo.uploadStatus !== UploadFileStatus.COMPLETE &&
        fileInfo.uploadStatus !== UploadFileStatus.FAILED
      ) {
        filesToUpload++;
      }
    }
    if (filesToUpload === 0) {
      return UploadManagerStatus.ERROR_ALL_FILES_PROCESSED;
    }
    return UploadManagerStatus.SUCCESS_UPLOAD_CAN_START;
  }

  private d(message: string, details?: any) {
    if (this.DEBUG) {
      if (details) {
        console.log(message, details);
      } else {
        console.log(message);
      }
    }
  }

  private reportProgress() {
    const progressInfo: IIndexedObject = {};
    let totalChunks = 0;
    let uploadedChunks = 0;
    // extract necessary information
    for (let f = 0; f < this.filesPending.length; f++) {
      const fileInfo = this.filesPending[f];
      if (!progressInfo[fileInfo.identifier]) {
        progressInfo[fileInfo.identifier] = {
          totaChunks: 0,
          uploadedChunks: 0,
        };
      }
      progressInfo[fileInfo.identifier].totaChunks += fileInfo.totalChunks;
      progressInfo[fileInfo.identifier].uploadedChunks +=
        fileInfo.uploadedChunks;

      totalChunks += fileInfo.totalChunks;
      uploadedChunks += fileInfo.uploadedChunks;
    }

    // iterate over the extracted information
    const progressItems: UploadManagerProgressReportItem[] = [];
    for (const key in progressInfo) {
      const info = progressInfo[key];
      progressItems.push(
        new UploadManagerProgressReportItem(
          key,
          info.totaChunks,
          info.uploadedChunks
        )
      );
    }

    this.progressCallback(
      new UploadManagerProgressReport(
        totalChunks,
        uploadedChunks,
        progressItems
      )
    );

    // prepare next call for progress
    if (this.uploadingInProgress) {
      setTimeout(() => this.reportProgress(), 1000);
    }
  }
}

export default UploadManager;
